import { useId, type ReactNode, type Ref, type SelectHTMLAttributes } from "react";
import { cn } from "../lib/cn";
import { FieldLabel } from "./FieldLabel";
import { useFormSkin } from "./FormSkinContext";
import { FIELD_SHELL_SKIN } from "./Input";
import { ChevronDownIcon, XIcon } from "./icons";

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  labelAddon?: ReactNode | undefined;
  /** Render `labelAddon` inline right after the label text instead of far-right. */
  labelAddonInline?: boolean | undefined;
  /**
   * Visually hide the label; it stays for screen readers. For a control whose
   * question is a visible heading right above it, where a second label reads
   * as a duplicate. The hint, when given, stays visible.
   */
  labelHidden?: boolean | undefined;
  ref?: Ref<HTMLSelectElement> | undefined;
  children: ReactNode;
}

/**
 * Styled <select>. Pair with react-hook-form via `<Select {...register("foo")}>...</Select>`.
 * Callers supply <option> elements as children.
 */
export function Select({
  ref,
  label,
  hint,
  error,
  required,
  labelAddon,
  labelAddonInline,
  labelHidden,
  className,
  id,
  children,
  ...rest
}: SelectProps): ReactNode {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const errorId = error ? `${selectId}-error` : undefined;
  const skin = useFormSkin();
  return (
    <div>
      <FieldLabel
        htmlFor={selectId}
        required={required}
        hint={hint}
        addon={labelAddon}
        addonInline={labelAddonInline}
        visuallyHidden={labelHidden}
      >
        {label}
      </FieldLabel>
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          {...rest}
          className={cn(
            "border-border bg-input-bg text-foreground block w-full border-2",
            FIELD_SHELL_SKIN[skin],
            // Both skins hide the blurry native arrow and draw the crisp
            // chevron icon below (styleguide section 14's dropdown specimen
            // has no native arrow); `pr-9` keeps the text clear of it.
            "appearance-none pr-9",
            "focus:border-primary focus:bg-white focus:outline-none",
            "disabled:text-cloud-blue disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-danger/40 bg-danger-bg focus:border-danger",
            className,
          )}
        >
          {children}
        </select>
        <ChevronDownIcon
          size={14}
          className={cn(
            "pointer-events-none absolute right-4 top-1/2 -translate-y-1/2",
            skin === "enroll" ? "text-ember" : "text-slate-teal",
          )}
        />
      </div>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-danger mt-1.5 flex items-center gap-1.5 text-[13px] leading-[1.5]"
        >
          <XIcon size={13} className="shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}
